import { isEqual } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { CampaignDto } from '@shared/services/apiService/apiService';
import {
  cloneConversationSettingsDraft,
  ConversationSettingsDraft,
  ConversationSettingsValidation,
  mapConversationSettingsDraftToRequest,
  mapConversationSettingsToDraft,
  validateConversationSettingsDraft,
} from '../utils/conversationSettings.utils';

type Result = {
  draft: ConversationSettingsDraft | null;
  setDraft: Dispatch<SetStateAction<ConversationSettingsDraft | null>>;
  resetDraft: () => void;
  isDirty: boolean;
  validationErrors: ConversationSettingsValidation;
};

export const useConversationSettingsController = (campaign?: CampaignDto): Result => {
  const [draft, setDraft] = useState<ConversationSettingsDraft | null>(null);
  const [serverDraft, setServerDraft] = useState<ConversationSettingsDraft | null>(null);
  const [initialized, setInitialized] = useState(false);
  const mapped = useMemo(
    () => mapConversationSettingsToDraft(campaign?.turnHandling, campaign?.speechPacing),
    [campaign?.speechPacing, campaign?.turnHandling],
  );
  const isDirty = useMemo(() => {
    if (!draft || !serverDraft) return false;
    const normalizedDraft = mapConversationSettingsDraftToRequest(draft);
    const comparableDraft =
      normalizedDraft.turnHandling === null ? { ...draft, turnHandling: null } : draft;
    return !isEqual(comparableDraft, serverDraft);
  }, [draft, serverDraft]);
  const validationErrors = useMemo(
    () =>
      draft ? validateConversationSettingsDraft(draft) : { turnHandling: {}, speechPacing: {} },
    [draft],
  );

  useEffect(() => {
    const next = cloneConversationSettingsDraft(mapped);
    setServerDraft(next);
    if (!initialized || !isDirty) {
      setDraft(cloneConversationSettingsDraft(next));
      setInitialized(true);
    }
  }, [initialized, isDirty, mapped]);

  const resetDraft = useCallback(() => {
    setDraft(serverDraft ? cloneConversationSettingsDraft(serverDraft) : null);
  }, [serverDraft]);

  return { draft, setDraft, resetDraft, isDirty, validationErrors };
};
